import React, { FC, forwardRef } from 'react';
import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  Tooltip,
  TooltipProps,
  CircularProgress,
} from '@mui/material';
import clsx from 'clsx';
import { combineSX } from 'helpers';

interface IconButtonPropsPart {
  tooltip?: TooltipProps['title'] | null;
  tooltipProps?: Partial<Omit<TooltipProps, 'title'>>;
  loading?: boolean;
  color?: Exclude<MuiIconButtonProps['color'], 'default'>;
}

export type IconButtonProps = IconButtonPropsPart &
  Omit<MuiIconButtonProps, keyof IconButtonPropsPart>;

const IconButton: FC<IconButtonProps> = forwardRef(
  (
    {
      size = 'medium',
      tooltip,
      tooltipProps,
      loading = false,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    const iconButton = (
      <MuiIconButton
        ref={ref}
        {...rest}
        disabled={rest.disabled || loading}
        size={size}
        sx={combineSX(
          {
            position: 'relative',
            margin: {
              small: '-3px',
              medium: '-6px',
              large: '-9px',
            }[size],
            padding: {
              small: '3px',
              medium: '6px',
              large: '9px',
            }[size],
          },
          sx,
        )}
      >
        {children}
        {loading && (
          <CircularProgress
            size={32}
            color="primary"
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              zIndex: 1,
            }}
          />
        )}
      </MuiIconButton>
    );

    return tooltip && !rest.disabled ? (
      <Tooltip title={tooltip} disableInteractive {...tooltipProps}>
        {iconButton}
      </Tooltip>
    ) : (
      <>{iconButton}</>
    );
  },
);

export default IconButton;
